import { spawnHitFx } from "@/entities/Skill";
import {
  spawnSkillFx,
  spawnBasicAttackFx,
  spawnDamageNumber,
} from "@/entities/SkillEffects";
import { ensureRealtime } from "@/network/realtime";
import { useCharacterStore } from "@/state/characterStore";
import { useUIStore } from "@/state/uiStore";
import { useDeathStore } from "@/state/deathStore";
import { useSkillStore, type SkillTemplate } from "@/state/skillStore";
import type {
  AttackResp,
  PlayerDeathPush,
  PlayerRespawnPush,
} from "@/network/messages";
import type { FieldScene } from "../FieldScene";
import { ATTACK, classRange, type SkillTpl } from "./FieldConstants";
import { handleExpGain } from "./FieldNetworkHandlers";

export function createFieldCombat(scene: FieldScene) {
  function classSkills(): SkillTpl[] {
    const ch = useCharacterStore.getState().current;
    if (!ch) return [];
    const skSt = useSkillStore.getState();
    const learned = skSt.learned;
    // Khớp với SkillBar UI: chỉ skill đã học, theo class, lấy 5 slot đầu.
    const list: SkillTemplate[] = skSt
      .byClass(ch.class)
      .filter((s) => (learned[s.id] ?? 0) > 0);
    return list.slice(0, 5).map((s) => ({
      id: s.id,
      name: s.name,
      class: s.class,
      cooldownMs: s.cooldownMs,
      manaCost: s.manaCost,
      rangeM: s.rangeM,
    }));
  }

  async function castHotkey(i: number) {
    if (scene.dead) return;
    const sk = classSkills()[i];
    if (!sk) return;
    if (!scene.cd.isReady(sk.id)) {
      useUIStore.getState().showToast(`${sk.name} đang hồi`, "warn");
      return;
    }
    const isHeal = sk.id === 3020;
    if (!isHeal && (!scene.targetMonster || !scene.targetMonster.alive)) {
      useUIStore.getState().showToast("Cần chọn mục tiêu", "warn");
      return;
    }
    // Mana gate (UI). Server is authoritative — chỉ cảnh báo nếu thiếu rõ rệt, không chặn cast.
    {
      const cur = useCharacterStore.getState().mp;
      if (sk.manaCost > 0 && cur + 1 < sk.manaCost) {
        useUIStore
          .getState()
          .showToast(
            `Không đủ mana (${Math.round(cur)}/${sk.manaCost})`,
            "warn",
          );
        return;
      }
    }
    const tgt = scene.targetMonster;
    const caster = scene.localPlayer
      ? { x: scene.localPlayer.x, y: scene.localPlayer.y }
      : undefined;
    // Range gate (UI). Server is authoritative. Skip for heal (self-cast).
    if (!isHeal && scene.localPlayer && tgt) {
      const dx = tgt.x - scene.localPlayer.x;
      const dy = tgt.y - scene.localPlayer.y;
      const ch2 = useCharacterStore.getState().current;
      const klass2 = ch2?.class ?? "warrior";
      const maxRange =
        sk.rangeM && sk.rangeM > 0 ? sk.rangeM * 32 : classRange(klass2);
      if (dx * dx + dy * dy > maxRange * maxRange) {
        useUIStore.getState().showToast("Mục tiêu ngoài tầm", "warn");
        return;
      }
    }
    try {
      const cli = await ensureRealtime();
      const reqPromise = cli.request<AttackResp>(ATTACK, {
        skillId: sk.id,
        targetId: tgt?.id ?? 0,
      });
      scene.cd.arm(sk.id, sk.cooldownMs);
      // Optimistic mana deduction; server snapshot will reconcile authoritative value.
      if (sk.manaCost > 0) {
        const st = useCharacterStore.getState();
        st.setVitals({ mp: Math.max(0, st.mp - sk.manaCost) });
      }
      const resp = await reqPromise;
      if (!resp.ok && resp.reason === "on_cooldown") {
        // Server rejected: re-arm UI cooldown to server's authoritative remaining time
        // so subsequent local checks match. Also clear optimistic mana refund.
        const remain = resp.cooldownRemainMs ?? resp.cooldownMs ?? sk.cooldownMs;
        scene.cd.arm(sk.id, Math.max(0, remain));
        if (sk.manaCost > 0) {
          const st = useCharacterStore.getState();
          st.setVitals({ mp: Math.min(st.maxMp, st.mp + sk.manaCost) });
        }
        useUIStore
          .getState()
          .showToast(`${sk.name} đang hồi`, "warn");
        return;
      }
      if (isHeal && resp.ok) {
        // Heal: show heal number on caster, update player HP.
        const cx = caster?.x ?? 0;
        const cy = caster?.y ?? 0;
        spawnSkillFx(scene, sk.id, {
          caster,
          target: { x: cx, y: cy },
          onImpact: () => {
            spawnDamageNumber(scene, cx, cy, resp.healAmount ?? 0, {
              crit: resp.crit,
              heal: true,
            });
          },
        });
        if (resp.newHp != null) {
          const st = useCharacterStore.getState();
          st.setVitals({ hp: resp.newHp });
          scene.localPlayer?.setHp(resp.newHp, st.maxHp);
        }
        return;
      }
      spawnSkillFx(scene, sk.id, {
        caster,
        target: { x: tgt!.x, y: tgt!.y },
        onImpact: () => {
          if (resp.ok) {
            if (tgt && tgt.alive) {
              tgt.flashHit({ fromX: caster?.x, fromY: caster?.y });
            }
            spawnDamageNumber(scene, tgt!.x, tgt!.y, resp.damage, {
              crit: resp.crit,
            });
          }
        },
      });
      if (resp.ok) {
        if (resp.killed) {
          tgt!.setHp(0);
          if (scene.targetMonster === tgt) scene.targetMonster = undefined;
        } else {
          tgt!.setHp(resp.targetHp);
        }
        handleExpGain(scene, resp);
      }
    } catch (e) {
      useUIStore
        .getState()
        .showToast(`Cast lỗi: ${(e as Error).message}`, "error");
    }
  }

  async function basicAttack() {
    if (scene.dead) return;
    if (!scene.cd.isReady(scene.BASIC_ATTACK_ID)) return;
    if (!scene.localPlayer) return;
    if (!scene.targetMonster || !scene.targetMonster.alive) {
      useUIStore.getState().showToast("Cần chọn mục tiêu", "warn");
      return;
    }
    const tgt = scene.targetMonster;
    const ch = useCharacterStore.getState().current;
    const klass = ch?.class ?? "warrior";
    const from = { x: scene.localPlayer.x, y: scene.localPlayer.y };
    const to = { x: tgt.x, y: tgt.y };
    {
      const dx = tgt.x - scene.localPlayer.x;
      const dy = tgt.y - scene.localPlayer.y;
      if (dx * dx + dy * dy > classRange(klass) ** 2) {
        useUIStore.getState().showToast("Mục tiêu ngoài tầm", "warn");
        return;
      }
    }
    scene.cd.arm(scene.BASIC_ATTACK_ID, scene.BASIC_ATTACK_CD_MS);

    // Fire visual immediately — independent of network result.
    let resp: AttackResp | null = null;
    spawnBasicAttackFx(scene, klass, from, to, () => {
      if (resp && resp.ok) {
        if (tgt && tgt.alive) {
          tgt.flashHit({ fromX: from.x, fromY: from.y });
        }
        spawnDamageNumber(scene, tgt.x, tgt.y, resp.damage, {
          crit: resp.crit,
        });
      } else {
        spawnHitFx(scene, tgt.x, tgt.y, { damage: 0 });
      }
    });

    ensureRealtime()
      .then((cli) =>
        cli.request<AttackResp>(ATTACK, { skillId: 0, targetId: tgt.id }),
      )
      .then((r) => {
        resp = r;
        if (r && r.ok) {
          if (r.killed) {
            tgt.setHp(0);
            if (scene.targetMonster === tgt) scene.targetMonster = undefined;
          } else {
            tgt.setHp(r.targetHp);
          }
          handleExpGain(scene, r);
        } else if (r && r.reason) {
          if (r.reason === "on_cooldown") {
            const remain =
              r.cooldownRemainMs ?? r.cooldownMs ?? scene.BASIC_ATTACK_CD_MS;
            scene.cd.arm(scene.BASIC_ATTACK_ID, Math.max(0, remain));
          } else {
            const label =
              r.reason === "out_of_range" ? "Mục tiêu ngoài tầm" : r.reason;
            useUIStore.getState().showToast(label, "warn");
          }
        }
      })
      .catch(() => {
        /* server may not support basic yet — visual already played */
      });
  }

  // Death / respawn are server-driven (FieldRoom broadcasts player_death and
  // player_respawn pushes). Client only renders the modal and applies the
  // authoritative payload. No local timer, no local spawn point.
  function onServerDeath(msg: PlayerDeathPush) {
    if (scene.localUid && msg.uid !== scene.localUid) return;
    if (scene.dead) return;
    scene.dead = true;
    scene.autoAttack = false;
    if (scene.targetMonster) {
      scene.targetMonster.setSelected(false);
      scene.targetMonster = undefined;
    }
    const st = useCharacterStore.getState();
    st.setVitals({ hp: 0 });
    if (scene.localPlayer) {
      scene.localPlayer.setHp(0, st.maxHp);
      scene.localPlayer.bodySprite.setAlpha(0.4);
      scene.localPlayer.bodySprite.setAngle(90);
    }
    useDeathStore
      .getState()
      .show(msg.respawnDelayMs, msg.message ?? "Bạn đã bị hạ gục.");
  }

  function onServerRespawn(msg: PlayerRespawnPush) {
    if (scene.localUid && msg.uid !== scene.localUid) return;
    const st = useCharacterStore.getState();
    st.setVitals({
      hp: msg.hp,
      mp: msg.mp,
      maxHp: msg.maxHp,
      maxMp: msg.maxMp,
    });
    st.setPosition({ x: msg.x, y: msg.y, mapId: msg.mapId });
    useDeathStore.getState().hide();
    // Map switch — let the target scene take over. Keep scene.dead=true so any
    // late-arriving field snapshots can't re-show the death modal between now
    // and FieldScene.shutdown. FieldScene.create() resets `dead` on next entry.
    if (msg.mapId === 1) {
      scene.scene.start("Town");
      return;
    }
    // Same / other field map — restart Field so the new map renders.
    scene.dead = false;
    if (scene.localPlayer) {
      scene.localPlayer.bodySprite.setAlpha(1);
      scene.localPlayer.bodySprite.setAngle(0);
      scene.localPlayer.setHp(msg.hp, msg.maxHp);
      scene.localPlayer.x = msg.x;
      scene.localPlayer.y = msg.y;
    }
  }

  return {
    classSkills,
    castHotkey,
    basicAttack,
    onServerDeath,
    onServerRespawn,
  };
}

export type FieldCombat = ReturnType<typeof createFieldCombat>;
